import { backboneService } from '@/services/backbone.js';
import { sseService } from '@/services/sse-service.js';
import { assetsClient } from '@/clients/assets-client.js';
import { imgCache } from '@/services/img-cache.js';
import { HatElement } from '@/components/hat-element.js';

class HatLocationProfile extends HatElement {
  private _data: any = null;
  private _locationId: string | null = null;
  private _isNew = false;
  private _consumerId: string | null = null;
  private _sseSubscribed = false;
  private _outsideClickHandler: ((e: MouseEvent) => void) | null = null;

  constructor() {
    super();
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  static get observedAttributes() {
    return ['location-id', 'is-new'];
  }

  connectedCallback() {
    super.connectedCallback();
    this._isNew = this.getAttribute('is-new') === 'true';

    if (this.hasAttribute('location-id')) {
      this._locationId = this.getAttribute('location-id');
      this._consumerId = `hat-location-profile-${this._locationId}`;
      this.loadData();

      if (this._isNew) {
        this._listenForSse();
      }
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (oldValue === newValue) return;

    if (name === 'is-new') {
      this._isNew = newValue === 'true';
    }

    if (name === 'location-id') {
      this._locationId = newValue;
      this._consumerId = `hat-location-profile-${this._locationId}`;
      if (this.isConnected) this.loadData();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._outsideClickHandler) {
      document.removeEventListener('click', this._outsideClickHandler);
    }
    if (this._sseSubscribed && this._consumerId) {
      sseService.unsubscribe(this._consumerId);
      this._sseSubscribed = false;
    }
  }

  // ─── Data ─────────────────────────────────────────────────────────────────

  async loadData() {
    if (!this._locationId) return;
    this._data = await assetsClient.getLocation(this._locationId);
    this._render();
    this._loadAssets();
  }

  async _loadAssets() {
    if (!this._locationId) return;

    const list = this.root.querySelector<HTMLElement>('.assets-list');
    if (!list) return;

    const assets: any[] = [];
    let pageToken: string | null = null;

    do {
      const result = await assetsClient.listAssets(this._locationId, pageToken);
      assets.push(...result.assets);
      pageToken = result.nextPageToken;
    } while (pageToken);

    list.innerHTML = '';

    if (!assets.length) {
      list.innerHTML = '<p style="color:#888;font-size:14px;">No assets yet.</p>';
      return;
    }

    await customElements.whenDefined('hat-asset-card');

    assets.forEach(asset => {
      const card = document.createElement('hat-asset-card');
      customElements.upgrade(card);
      (card as HatAssetCardElement).setData(asset);
      list.appendChild(card);
    });

    list.addEventListener('delete', () => this._loadAssets(), { once: true });
  }

  _listenForSse() {
    if (!this._consumerId) return;

    this._sseSubscribed = true;
    sseService.subscribe(this._consumerId, 'refreshRequest', (data: any) => {
      const matched = (data.directives || []).some(
        (d: any) => d.domainSpace === 'LOCATION' && d.domainId === this._locationId
      );
      if (!matched) return;

      console.log('[hat-location-profile] SSE refresh received', data);

      const invalidatedDocIds = (data.directives || [])
        .filter((d: any) => d.directiveName === 'INVALIDATE' && d.domainSpace === 'DOCUMENT')
        .map((d: any) => d.domainId);

      if (invalidatedDocIds.length > 0) {
        imgCache.revokeUrlCache(invalidatedDocIds);
      }

      this.loadData();
    });
  }

  // ─── Rendering ────────────────────────────────────────────────────────────

  _render() {
    if (!this._data) return;

    const { name, address, displayImage, images } = this._data;

    const addressText =
      address && Object.keys(address).length
        ? [address.street1, address.street2, address.city, address.state, address.postalCode]
            .filter(Boolean)
            .join(', ')
        : 'No address set';

    this.root.innerHTML = `
      <style>
        .container {
          max-width: 1000px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .top-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .menu {
          position: relative;
        }

        .menu-btn {
          padding: 6px 12px;
          border-radius: 8px;
          border: 1px solid #ddd;
          background: #f7f7f7;
          cursor: pointer;
          font-size: 16px;
        }

        .menu-btn:hover { background: #ececec; }

        .dropdown {
          position: absolute;
          right: 0;
          top: 42px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          display: none;
          flex-direction: column;
          min-width: 140px;
          overflow: hidden;
          z-index: 10;
        }

        .dropdown button {
          padding: 8px 12px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
        }

        .dropdown button:hover { background: #f2f2f2; }

        .menu.open .dropdown { display: flex; }

        .profile {
          background: #fff;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 6px 20px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .top {
          display: flex;
          gap: 20px;
          align-items: flex-start;
        }

        .info { flex: 1; min-width: 0; }

        .name {
          font-size: 22px;
          font-weight: 600;
          word-break: break-word;
        }

        .address {
          color: #666;
          font-size: 14px;
          word-break: break-word;
        }

        .hero-image {
          width: min(260px, 100%);
          border-radius: 16px;
          overflow: hidden;
          flex-shrink: 1;
          align-self: flex-start;
        }

        .hero-image hat-image {
          width: 100%;
          height: auto;        /* was 100%, change to auto */
          display: block;
        }

        .section {
          background: #fff;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.06);
        }

        .assets-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .add-btn {
          padding: 6px 12px;
          border-radius: 8px;
          border: 1px solid #ddd;
          background: #f7f7f7;
          cursor: pointer;
        }

        .add-btn:hover { background: #ececec; }

        .modal {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.92);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          box-sizing: border-box;
        }

        .modal hat-image {
          width: auto;
          height: auto;
          max-width: 90vw;
          max-height: 90vh;
          border-radius: 12px;
        }

        @media (max-width: 700px) {
          .top { flex-direction: column; align-items: flex-start; }
          .info { width: 100%; }
        }
      </style>

      <div class="container">
        <div class="top-bar">
          <hat-nav-button icon="arrow_back" href="/app">Back</hat-nav-button>
          <div class="menu" hat-entitlement="feature.locations.edit feature.locations.delete">
            <button class="menu-btn">⋮</button>
            <div class="dropdown">
              <button data-action="edit" hat-entitlement="feature.locations.edit">Edit</button>
              <button data-action="delete" hat-entitlement="feature.locations.delete">Delete</button>
            </div>
          </div>
        </div>

        <div class="profile">
          <div class="top">
            <div class="hero-image">
              <hat-image
                type="fullsize"
                document-id="${displayImage?.thumbnailDocId || ''}">
              </hat-image>
            </div>
            <div class="info">
              <div class="name">${name || addressText}</div>
              ${name ? `<div class="address">${addressText}</div>` : ''}
            </div>
          </div>

          <hat-location-gallery mode="view"></hat-location-gallery>
        </div>

        <div class="section">
          <div class="section-header">
            <h3>Assets</h3>
            <button class="add-btn" hat-entitlement="feature.asset.new">+ Add Asset</button>
          </div>
          <div class="assets-list"></div>
        </div>
      </div>
    `;

    // Populate the gallery now that the DOM exists
    const gallery = this.root.querySelector('hat-location-gallery')!;
    gallery.setImages(images || []);

    this._setupEvents();
    this._applyEntitlements();
  }

  // ─── Events ───────────────────────────────────────────────────────────────

  _setupEvents() {
    const menu    = this.root.querySelector<HTMLElement>('.menu')!;
    const button  = menu.querySelector<HTMLElement>('.menu-btn')!;
    const gallery = this.root.querySelector('hat-location-gallery')!;

    button.onclick = (e) => {
      e.stopPropagation();
      menu.classList.toggle('open');
    };

    menu.querySelector<HTMLElement>('[data-action="edit"]')!.onclick = () => {
      menu.classList.remove('open');
      backboneService.navigateTo(`/app/locations/${this._locationId}/edit`);
    };

    menu.querySelector<HTMLElement>('[data-action="delete"]')!.onclick = async () => {
      menu.classList.remove('open');
      await assetsClient.deleteLocation(this._locationId!);
      this.dispatchEvent(new CustomEvent('delete', {
        detail: { id: this._locationId },
        bubbles: true,
        composed: true
      }));
      backboneService.navigateTo('/app');
    };

    this.root.querySelector<HTMLElement>('.add-btn')!.onclick = () => {
      backboneService.navigateTo(`/app/locations/${this._locationId}/assets/new`);
    };

    gallery.addEventListener('image-expanded', (e: Event) => {
      this._openImage((e as CustomEvent).detail);
    });

    this._outsideClickHandler = (e) => {
      if (!e.composedPath().includes(menu)) menu.classList.remove('open');
    };
    document.addEventListener('click', this._outsideClickHandler);
  }

  // ─── Modal ────────────────────────────────────────────────────────────────

  _openImage({ documentId }: { documentId?: string }) {
    const modal = document.createElement('div');
    modal.className = 'modal';

    const img = document.createElement('hat-image');
    img.setAttribute('type', 'expanded');
    img.setAttribute('document-id', documentId ?? '');

    modal.appendChild(img);
    modal.onclick = () => modal.remove();
    this.root.appendChild(modal);
  }
}

customElements.define('hat-location-profile', HatLocationProfile);